
import { useState } from "react";
import { UnsplashImageSearch } from "@/components/UnsplashImageSearch";
import { UnsplashImagePickerModal } from "@/components/blog-management/UnsplashImagePickerModal";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { X, Info } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

type SelectedImage = {
  url: string;
  description: string;
  attribution: {
    photographerName: string;
    photographerUsername: string;
    photographerUrl: string;
    unsplashUrl: string;
  };
  width: number;
  height: number;
};

export default function UnsplashTestPage() {
  const [selectedImage, setSelectedImage] = useState<SelectedImage | null>(null);
  const [activeTab, setActiveTab] = useState("search");

  const handleImageSelect = (imageData: SelectedImage) => {
    setSelectedImage(imageData);
  };

  const handleClearImage = () => {
    setSelectedImage(null);
  };

  return (
    <div className="container mx-auto py-8">
      <h1 className="text-2xl font-bold mb-2">Unsplash Image Integration</h1>
      <p className="text-slate-500 mb-6">Test the Unsplash API integration with image search and selection</p>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="search">Search Component</TabsTrigger>
          <TabsTrigger value="modal">Modal Picker</TabsTrigger>
          <TabsTrigger value="integration">Blog Integration</TabsTrigger>
        </TabsList>
        
        <TabsContent value="search">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h2 className="text-lg font-medium mb-4">Search Images</h2>
              <Card className="p-4">
                <UnsplashImageSearch onImageSelect={handleImageSelect} />
              </Card>
            </div>
            
            <div>
              <h2 className="text-lg font-medium mb-4">Selected Image</h2>
              <Card className="p-4">
                {selectedImage ? (
                  <div className="space-y-4">
                    <div className="relative">
                      <img 
                        src={selectedImage.url} 
                        alt={selectedImage.description} 
                        className="w-full h-auto rounded-md"
                      />
                      <Button 
                        variant="destructive" 
                        size="icon" 
                        className="absolute top-2 right-2" 
                        onClick={handleClearImage}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <div>
                      <h3 className="font-medium">{selectedImage.description || "Untitled Image"}</h3>
                      
                      <div className="text-sm mt-1">
                        <p>Dimensions: {selectedImage.width} × {selectedImage.height}</p>
                      </div>
                      
                      <Separator className="my-4" />
                      
                      <div className="text-xs text-slate-500">
                        Photo by{" "}
                        <a 
                          href={selectedImage.attribution.photographerUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="underline hover:text-slate-700"
                        >
                          {selectedImage.attribution.photographerName}
                        </a>{" "}
                        on{" "}
                        <a 
                          href={selectedImage.attribution.unsplashUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="underline hover:text-slate-700"
                        >
                          Unsplash
                        </a>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <p>No image selected</p>
                    <p className="text-xs mt-2">Search and select an image from the left panel</p>
                  </div>
                )}
              </Card>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="modal">
          <Card className="p-6">
            <h2 className="text-lg font-medium mb-4">Modal Image Picker</h2>
            <p className="text-slate-500 mb-4">
              This component can be easily integrated into forms to select images from Unsplash
            </p>
            
            <div className="space-y-6">
              <UnsplashImagePickerModal onImageSelect={handleImageSelect} />
              
              {selectedImage && (
                <div className="p-4 border rounded-md">
                  <div className="flex flex-col md:flex-row gap-4">
                    <img 
                      src={selectedImage.url} 
                      alt={selectedImage.description} 
                      className="w-full md:w-1/3 h-auto rounded" 
                    />
                    <div className="space-y-2">
                      <h3 className="font-medium">{selectedImage.description || "Untitled Image"}</h3>
                      <p className="text-sm text-slate-500">
                        Dimensions: {selectedImage.width} × {selectedImage.height}
                      </p>
                      <div className="text-xs text-slate-500 pt-2 border-t">
                        Photo by{" "}
                        <a 
                          href={selectedImage.attribution.photographerUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="underline hover:text-slate-700"
                        >
                          {selectedImage.attribution.photographerName}
                        </a>{" "}
                        on{" "}
                        <a 
                          href={selectedImage.attribution.unsplashUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="underline hover:text-slate-700"
                        >
                          Unsplash
                        </a>
                      </div>
                      
                      <Button 
                        variant="destructive" 
                        size="sm" 
                        className="mt-2" 
                        onClick={handleClearImage}
                      >
                        <X className="h-3 w-3 mr-1" />
                        Clear Image
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>
        
        <TabsContent value="integration">
          <Card className="p-6">
            <h2 className="text-lg font-medium mb-4">Blog Integration</h2>
            <Alert className="mb-6">
              <Info className="h-4 w-4" />
              <AlertDescription>
                The Unsplash image picker has been integrated with the blog post editor.
                When creating or editing a blog post, you can select images from Unsplash
                and proper attribution will be automatically added.
              </AlertDescription>
            </Alert>
            
            <div className="space-y-4">
              <h3 className="font-medium">Integration Features:</h3>
              <ul className="list-disc list-inside space-y-2 text-slate-600">
                <li>Select images directly from Unsplash in the blog post form</li>
                <li>Image attribution is stored in the post metadata</li>
                <li>Attribution is displayed according to Unsplash guidelines</li>
                <li>Support for clearing and changing selected images</li>
              </ul>
              
              <div className="mt-6">
                <Button 
                  onClick={() => window.location.href = "/dashboard/blog/posts/new"}
                >
                  Create a New Blog Post
                </Button>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
